<template>
  <div class="px-6 py-2" data-cy="agent-access-page">
    <MainHeader content="AI Agent Access" />

    <v-alert v-if="!isAdmin" type="info" variant="tonal" class="mt-4" data-cy="agent-access-denied">
      Only a firm administrator can change agent access.
    </v-alert>

    <template v-else>
      <p class="text-body-2 text-grey-darken-2 mb-4">
        Agents can only be connected by people at your firm after you approve them here.
      </p>

      <LlTabs v-model="uiState.tab" :items="agentTabs" class="mb-4" />

      <v-skeleton-loader v-if="loading" type="table-row-divider@4" class="w-full" elevation="0" />

      <v-window v-else v-model="uiState.tab">
        <v-window-item value="agents">
          <LlDataTable
            :headers="agentHeaders"
            :items="agents"
            :items-length="agents.length"
            item-value="clientId"
            :must-sort="false"
            data-cy="agents-table"
          >
            <template #item.name="{ item }">
              <div class="py-2">
                <div class="font-weight-medium">{{ item.name }}</div>
                <div class="text-caption text-grey-darken-1">{{ item.origin }}</div>
                <div class="mt-1">
                  <LlStatusBadge :tone="item.isVerifiedPublisher ? 'green' : 'amber'">
                    {{ item.isVerifiedPublisher ? 'Verified publisher' : 'Publisher not verified' }}
                  </LlStatusBadge>
                </div>
              </div>
            </template>

            <template #item.status="{ item }">
              <LlStatusBadge :tone="STATUS_TONES[item.status]" :data-cy="`status-${item.clientId}`">
                {{ STATUS_LABELS[item.status] }}
              </LlStatusBadge>
              <div v-if="decidedBy(item)" class="text-caption text-grey-darken-1 mt-1">
                {{ decidedBy(item) }}
              </div>
            </template>

            <template #item.permissions="{ item }">
              <span v-if="item.status !== 'approved'" class="text-grey">&mdash;</span>
              <span v-else>{{ capSummary(item) }}</span>
            </template>

            <template #item.connections="{ item }">
              <span v-if="item.status !== 'approved'" class="text-grey">&mdash;</span>
              <span v-else>{{ item.activeConnectionCount }}</span>
            </template>

            <template #item.actions="{ item }">
              <div class="text-right whitespace-nowrap">
                <template v-if="item.status === 'approved'">
                  <LlSecondaryButton
                    size="small"
                    :data-cy="`permissions-${item.clientId}`"
                    @click="openApprove(item)"
                  >
                    Permissions
                  </LlSecondaryButton>
                  <LlSecondaryButton
                    size="small"
                    color="error"
                    class="ml-2"
                    :data-cy="`withdraw-${item.clientId}`"
                    @click="askWithdraw(item)"
                  >
                    Withdraw
                  </LlSecondaryButton>
                </template>
                <LlSecondaryButton
                  v-else-if="item.status === 'withdrawn'"
                  size="small"
                  :data-cy="`restore-${item.clientId}`"
                  @click="askRestore(item)"
                >
                  Restore
                </LlSecondaryButton>
                <LlSecondaryButton
                  v-else
                  size="small"
                  color="primary"
                  :data-cy="`approve-${item.clientId}`"
                  @click="openApprove(item)"
                >
                  Approve
                </LlSecondaryButton>
              </div>
            </template>
          </LlDataTable>
        </v-window-item>

        <v-window-item value="connections">
          <p v-if="!connections.length" class="text-body-2 text-grey-darken-1 py-8 text-center" data-cy="no-connections">
            No live connections. People connect an agent from within the agent itself, once you have
            approved it.
          </p>
          <LlDataTable
            v-else
            :headers="connectionHeaders"
            :items="connections"
            :items-length="connections.length"
            item-value="id"
            :must-sort="false"
            data-cy="connections-table"
          >
            <template #item.userName="{ item }">
              <div class="py-1">
                <div>{{ item.userName }}</div>
                <div class="text-caption text-grey-darken-1">{{ item.userEmail }}</div>
              </div>
            </template>

            <template #item.grantedScopes="{ item }">
              <a
                href="#"
                class="text-blue-darken-2"
                :data-cy="`inspect-${item.id}`"
                @click.prevent="inspect(item)"
              >{{ item.grantedScopes.length }}</a>
            </template>

            <template #item.lastUsedAtUtc="{ item }">
              <span v-if="item.lastUsedAtUtc">{{ getShortDateString(item.lastUsedAtUtc) }}</span>
              <span v-else class="text-grey">Never</span>
            </template>

            <template #item.actions="{ item }">
              <div class="text-right">
                <LlSecondaryButton
                  size="small"
                  color="error"
                  :data-cy="`revoke-${item.id}`"
                  @click="askRevoke(item)"
                >
                  Revoke
                </LlSecondaryButton>
              </div>
            </template>
          </LlDataTable>
        </v-window-item>
      </v-window>
    </template>

    <ApproveAgentDialog
      v-model="uiState.showApprove"
      :agent="uiState.selectedAgent"
      :permissions="permissions"
      :saving="saving"
      @approve="approve"
    />

    <ConnectionPermissionsDialog
      v-model="uiState.showConnection"
      :connection="uiState.selectedConnection"
      :permissions="permissions"
    />

    <LlConfirmationDialog
      ref="withdrawDialog"
      title="Withdraw this agent?"
      :message="withdrawMessage"
      confirm-text="Withdraw"
      confirm-color="error"
      data-cy="withdraw-dialog"
      preserve-newlines
      @confirm="withdraw"
    />

    <LlConfirmationDialog
      ref="restoreDialog"
      title="Restore this agent?"
      :message="restoreMessage"
      confirm-text="Restore"
      confirm-color="primary"
      data-cy="restore-dialog"
      preserve-newlines
      @confirm="restore"
    />

    <LlConfirmationDialog
      ref="revokeDialog"
      title="Revoke this connection?"
      :message="revokeMessage"
      confirm-text="Revoke"
      confirm-color="error"
      data-cy="revoke-dialog"
      preserve-newlines
      @confirm="revoke"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { storeToRefs } from 'pinia';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import LlDataTable from '@/components/LlDataTable.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlStatusBadge from '@/components/LlStatusBadge.vue';
import LlTabs from '@/components/LlTabs.vue';
import MainHeader from '@/components/MainHeader.vue';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { useMeStore } from '@/services/UserService/useMeStore';
import { getShortDateString } from '@/services/utils';
import ApproveAgentDialog from './components/ApproveAgentDialog.vue';
import ConnectionPermissionsDialog from './components/ConnectionPermissionsDialog.vue';
import { useAgentAccessStore } from './stores/useAgentAccessStore';
import type {
  AgentAccessAgent,
  AgentAccessConnection,
  AgentAccessPermission,
  AgentStatus,
} from './models/agentAccessTypes';

const STATUS_LABELS: Record<AgentStatus, string> = {
  none: 'Not approved',
  approved: 'Approved',
  withdrawn: 'Withdrawn',
};

const STATUS_TONES: Record<AgentStatus, 'green' | 'red' | 'grey'> = {
  none: 'grey',
  approved: 'green',
  withdrawn: 'red',
};

const agentHeaders = [
  { title: 'Agent', key: 'name', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
  { title: 'Permissions', key: 'permissions', sortable: false },
  { title: 'Connections', key: 'connections', sortable: false },
  { title: '', key: 'actions', sortable: false },
];

const connectionHeaders = [
  { title: 'User', key: 'userName', sortable: false },
  { title: 'Agent', key: 'agentName', sortable: false },
  { title: 'Permissions', key: 'grantedScopes', sortable: false },
  { title: 'Last Used', key: 'lastUsedAtUtc', sortable: false },
  { title: '', key: 'actions', sortable: false },
];

const meStore = useMeStore();
const { me } = storeToRefs(meStore);
const agentAccessStore = useAgentAccessStore();
const { agents, connections, permissions, loading, saving } = storeToRefs(agentAccessStore);

const agentTabs = computed(() => [
  { value: 'agents', label: 'Agents', dataCy: 'tab-agents' },
  {
    value: 'connections',
    label: `Connections (${connections.value.length})`,
    dataCy: 'tab-connections',
  },
]);
const { handleError, handleSuccess } = useSnackbarHandler();

const withdrawDialog = ref();
const restoreDialog = ref();
const revokeDialog = ref();

const uiState = reactive({
  tab: 'agents',
  showApprove: false,
  showConnection: false,
  selectedAgent: null as AgentAccessAgent | null,
  selectedConnection: null as AgentAccessConnection | null,
});

// access.setup is already true for a legacy `admin` role — the API folds both into it — so this is
// Account.IsAdmin(), not a narrower reading of it.
const isAdmin = computed(() => !!me.value?.access?.setup);

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

// Counted the way the permission picker ticks its boxes: wildcards unfold into what they cover and are
// not counted themselves, staying connected is always in, and no cap (null) is everything except the
// permissions that have to be named — so the number matches what an administrator sees on opening it.
const isWildcard = (scope: string) => scope.endsWith(':*');

const coversPermission = (scopes: string[] | null, p: AgentAccessPermission) => {
  if (p.scope === 'offline_access') return true;
  if (!scopes) return !p.requiresExplicitGrant;
  if (scopes.includes(p.scope)) return true;
  if (p.requiresExplicitGrant) return false;
  return scopes.includes('write:*') || (!p.isWrite && scopes.includes('read:*'));
};

const capSummary = (agent: AgentAccessAgent) => {
  const detailed = permissions.value.filter((p) => !isWildcard(p.scope));
  const withheld = detailed.filter((p) => !coversPermission(agent.allowedScopes, p));
  if (!withheld.length) return 'All permissions';
  if (withheld.every((p) => p.requiresExplicitGrant)) return 'All except sending email';
  return `${detailed.length - withheld.length} of ${detailed.length}`;
};

const decidedBy = (agent: AgentAccessAgent) => {
  if (agent.status === 'withdrawn' && agent.withdrawnBy) {
    return `${agent.withdrawnBy}, ${getShortDateString(agent.withdrawnAtUtc)}`;
  }
  if (agent.status === 'approved' && agent.approvedBy) {
    return `${agent.approvedBy}, ${getShortDateString(agent.approvedAtUtc)}`;
  }
  return '';
};

const withdrawMessage = computed(() => {
  const agent = uiState.selectedAgent;
  if (!agent) return '';
  const count = agent.activeConnectionCount;
  const ends = count
    ? `This ends ${plural(count, 'active connection')} straight away. Those agents lose access immediately, not just at next sign-in.`
    : 'There are no active connections to end.';
  return `${ends}\n\nNobody at your firm will be able to connect ${agent.name} until you approve it again.\n\nReversible — restoring the approval brings those connections back with the permissions people already gave, without asking them again.`;
});

const restoreMessage = computed(() => {
  const agent = uiState.selectedAgent;
  if (!agent) return '';
  return `People at your firm will be able to connect ${agent.name} again, and any connections withdrawn earlier will start working again with the permissions already approved — nobody is asked to consent a second time.\n\nRecorded against your name on the firm's audit trail.`;
});

const revokeMessage = computed(() => {
  const connection = uiState.selectedConnection;
  if (!connection) return '';
  const first = connection.userName.split(' ')[0];
  return `${connection.userName} (${connection.userEmail}) loses access through ${connection.agentName} straight away.\n\nThis one is not reversible. ${first} can reconnect from inside the agent, which creates a new connection and a fresh consent.`;
});

const load = async () => {
  try {
    await agentAccessStore.load();
  } catch (error) {
    handleError(error);
  }
};

onMounted(async () => {
  try {
    await meStore.getMe();
  } catch (error) {
    handleError(error);
    return;
  }
  if (isAdmin.value) {
    await load();
  }
});

const openApprove = (agent: AgentAccessAgent) => {
  uiState.selectedAgent = agent;
  uiState.showApprove = true;
};

const askWithdraw = (agent: AgentAccessAgent) => {
  uiState.selectedAgent = agent;
  withdrawDialog.value?.open();
};

const askRestore = (agent: AgentAccessAgent) => {
  uiState.selectedAgent = agent;
  restoreDialog.value?.open();
};

const askRevoke = (connection: AgentAccessConnection) => {
  uiState.selectedConnection = connection;
  revokeDialog.value?.open();
};

const inspect = (connection: AgentAccessConnection) => {
  uiState.selectedConnection = connection;
  uiState.showConnection = true;
};

const approve = async ({ clientId, allowedScopes }: { clientId: string; allowedScopes: string[] | null }) => {
  const editing = uiState.selectedAgent?.status === 'approved';
  try {
    await agentAccessStore.approve(clientId, allowedScopes);
    uiState.showApprove = false;
    handleSuccess(editing ? 'Permissions updated' : 'Agent approved');
  } catch (error) {
    handleError(error);
  }
};

const withdraw = async () => {
  if (!uiState.selectedAgent) return;
  try {
    await agentAccessStore.withdraw(uiState.selectedAgent.clientId);
    handleSuccess('Agent withdrawn');
  } catch (error) {
    handleError(error);
  }
};

const restore = async () => {
  if (!uiState.selectedAgent) return;
  try {
    await agentAccessStore.restore(uiState.selectedAgent.clientId);
    handleSuccess('Agent restored');
  } catch (error) {
    handleError(error);
  }
};

const revoke = async () => {
  if (!uiState.selectedConnection) return;
  try {
    await agentAccessStore.revokeConnection(uiState.selectedConnection.id);
    handleSuccess('Connection revoked');
  } catch (error) {
    handleError(error);
  }
};
</script>
